// src/history/HistoryTable.js
import React from 'react';
import {Box, Button, CircularProgress, Typography, Checkbox} from '@mui/material';
import {Stack, IconButton, Chip, Divider, Tooltip} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditableCell from '../UseTool/EditableCell'; // adjust path if needed
import EditableHeader from '../EditableHeader';
import {useTranslation} from 'react-i18next';
import {ToggleButton, ToggleButtonGroup} from '@mui/material';
import {alpha} from '@mui/material/styles';

// helpers to align values with the header columns
const norm = (s) => (s ?? "").toString().trim().toLowerCase().replace(/[：:]+$/g, "");
const getLeafLabels = (headers) => {
    if (!headers?.length) return [];
    const rows = headers.length, cols = headers[0].length;
    const labels = [];
    for (let c = 3; c < cols; c++) { // skip 3 fixed (Extracted, Updated, Filename)
        let label = "";
        for (let r = rows - 1; r >= 0; r--) {
            const v = headers[r][c];
            if (v && String(v).trim()) {
                label = String(v);
                break;
            }
        }
        if (!label) {
            for (let r = 0; r < rows; r++) {
                const v = headers[r][c];
                if (v && String(v).trim()) {
                    label = String(v);
                    break;
                }
            }
        }
        labels.push(label);
    }
    return labels;
};


function computeSpans(headers) {
    const numRows = headers.length;
    const numCols = headers[0].length;
    const skip = Array.from({length: numRows}, () => Array(numCols).fill(false));
    const result = [];
    for (let row = 0; row < numRows; row++) {
        const rowCells = [];
        for (let col = 0; col < numCols; col++) {
            if (skip[row][col]) continue;
            const value = headers[row][col];

            // Don't skip empty cells - we need to render them so they can be edited
            let rowSpan = 1, colSpan = 1;

            // Only compute rowSpan/colSpan for non-empty values
            if (value) {
                for (let r = row + 1; r < numRows; r++) {
                    if (!headers[r][col]) {
                        rowSpan++;
                        skip[r][col] = true;
                    } else break;
                }
                for (let c = col + 1; c < numCols; c++) {
                    const v2 = headers[row][c];

                    // allow merging if blank OR identical
                    const canMergeByValue = (v2 === '' || v2 === value);
                    if (!canMergeByValue) break;

                    // require same parents (rows above must match)
                    let sameParents = true;
                    for (let pr = 0; pr < row; pr++) {
                        if (headers[pr][col] !== headers[pr][c]) {
                            sameParents = false;
                            break;
                        }
                    }
                    if (!sameParents) break;

                    colSpan++;
                    skip[row][c] = true;
                }
            }

            rowCells.push({value, rowSpan, colSpan});
        }
        result.push(rowCells);
    }
    return result;
}

// Swiss date-time: DD.MM.YYYY HH:MM (24h), Europe/Zurich
const fmt = (iso) => {
    if (!iso) return '';
    return new Intl.DateTimeFormat('de-CH', {
        timeZone: 'Europe/Zurich',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
    }).format(new Date(iso)).replace(', ', ' ');
};

function PaginationBar({
                           page,
                           hasNext,
                           hasPrev,
                           onNext,
                           onPrev,
                           loading,
                           total,
                           pageSize,
                           onPageSizeChange,
                           rightExtra,
                           leftExtra
                       }) {

    const {t} = useTranslation();
    const hasTotals = Number.isFinite(total) && Number.isFinite(pageSize) && pageSize > 0;
    const totalPages = hasTotals ? Math.max(1, Math.ceil(total / pageSize)) : null;
    const label = hasTotals ? `${page} / ${totalPages}` : `${page}`;

    return (
        <Stack
            direction="row"
            alignItems="center"
            sx={{my: 1, width: '100%'}}
            justifyContent="space-between"
        >
            {/* Left: optional actions (bulk delete, etc.) */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 200}}>
                {leftExtra ?? null}
            </Stack>

            {/* Center: arrows + page label + compact page-size pills */}
            <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title={t('pagination.previousPage')}>
          <span>
            <IconButton size="small" onClick={onPrev} disabled={!hasPrev || loading}>
              <ChevronLeftIcon fontSize="small"/>
            </IconButton>
          </span>
                </Tooltip>

                <Typography variant="body2" sx={{minWidth: 40, textAlign: 'center'}}>
                    {label}
                </Typography>

                <Tooltip title={t('pagination.nextPage')}>
          <span>
            <IconButton size="small" onClick={onNext} disabled={!hasNext || loading}>
              <ChevronRightIcon fontSize="small"/>
            </IconButton>
          </span>
                </Tooltip>

                {onPageSizeChange && (
                    <Stack direction="row" alignItems="center" sx={{ml: 2}}>
                        <ToggleButtonGroup
                            value={pageSize}
                            exclusive
                            size="small"
                            onChange={(e, value) => value && onPageSizeChange(value)}
                        >
                            {[10, 20, 30, 50].map((size) => (
                                <ToggleButton key={size} value={size}>{size}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>

                    </Stack>
                )}

                {loading && <CircularProgress size={16} sx={{ml: 1}}/>}
            </Stack>

            {/* Right: total + optional actions */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 120, justifyContent: 'flex-end'}}>
                <Typography variant="caption" color="text.secondary" sx={{textAlign: 'right'}}>
                    {Number.isFinite(total) ? `${t('pagination.total')}: ${total} ${t('pagination.rows')}` : ''}
                </Typography>
                {rightExtra ?? null}
            </Stack>
        </Stack>
    );
}


export default function HistoryTable({
                                         loading,
                                         results,
                                         toolDetails,
                                         editedValues,
                                         editingCell,
                                         setEditingCell,
                                         editValue,
                                         setEditValue,
                                         handleConfirm,
                                         handleCancel,
                                         theme,
                                         onFilenameClick,
                                         page, hasNext, hasPrev, onNext, onPrev,
                                         total, pageSize, onPageSizeChange,
                                         selectedFile,
                                         selectedExecutionId,
                                         onCellClick,
                                         onCellDoubleClick,
                                         onCellHover,
                                         hoveredKeyExternal,
                                         onCellRedraw,
                                         overrides,
                                         extractingCell,
                                         processingCell,
                                         rightExtra,
                                         leftExtra,
                                         onToggleRow,
                                         selectedIds,
                                         onHeaderEdit
                                     }) {

    const {t} = useTranslation();
    const theadRef = React.useRef(null);
    const clickTimerRef = React.useRef(null);
    const [editingHeader, setEditingHeader] = React.useState(null); // {row, col}

    // Always compute headers/memos unconditionally (no early return before hooks)
    const rawHeaders = toolDetails?.headerConfig ?? [];
    const headers = React.useMemo(() => {
        console.log('[HistoryTable] Recomputing headers from rawHeaders:', rawHeaders);
        const safe = (val, fallback) => {
            const v = (val ?? '').toString().trim();
            return v.length ? v : fallback;
        };

        const h = JSON.parse(JSON.stringify(rawHeaders));
        h.forEach((row, idx) => {
            if (idx === 0) {
                // ensure non-empty labels so computeSpans doesn't drop the cells
                const extracted = safe(t('historyTable.extracted'), 'Extracted');
                const updated = safe(t('historyTable.updated'), 'Last update');
                const filename = safe(t('historyTable.filename'), 'Filename');

                // unshift so visible order becomes: Extracted | Last update | Filename
                row.unshift(filename);
                row.unshift(updated);
                row.unshift(extracted);
            } else {
                // keep these empty so computeSpans merges (row/col spans) correctly
                row.unshift('');
                row.unshift('');
                row.unshift('');
            }
        });
        return h;
    }, [rawHeaders, t]);
    console.log('All header rows =>', headers);
    headers.forEach((row, idx) => {
        console.log(`header row ${idx} =>`, row);
    });

// at top of the file (or inline) – tiny component for "select all"
    function SelectAllHeader({results, selectedIds, onToggleRow}) {
        const all = results?.length > 0 && results.every(r => selectedIds?.has?.(r?.data?.execution_id));
        return (
            <Checkbox
                size="small"
                checked={!!all}
                indeterminate={!all && (selectedIds?.size > 0)}
                inputProps={{'aria-label': 'Select all'}}
                onChange={(e) => {
                    const checked = e.target.checked;
                    if (onToggleRow && results) {
                        results.forEach(rr => onToggleRow(rr?.data?.execution_id, checked));
                    }
                }}
            />
        );
    }

// …

    const headersWithSelect = React.useMemo(() => {
        if (!headers.length) return [];
        return headers.map((row, idx) => {
            const r = [...row];
            if (idx === 0) {
                // Give the first row a truthy value so computeSpans creates a rowSpan
                r.unshift(
                    <SelectAllHeader
                        results={results}
                        selectedIds={selectedIds}
                        onToggleRow={onToggleRow}
                    />
                );
            } else {
                // Lower rows must be empty strings so they merge vertically into the first row
                r.unshift('');
            }
            return r;
        });
    }, [headers, results, selectedIds, onToggleRow]);


    const hasHeaders = headers.length > 0 && headers[0].length >= 3;

    const headerSpans = React.useMemo(() => {
        const spans = hasHeaders ? computeSpans(headersWithSelect) : [];
        console.log('headerSpans after computeSpans =>', spans);
        spans.forEach((row, idx) => {
            console.log(`headerSpans row ${idx} =>`, row.map(cell => ({value: cell.value, colSpan: cell.colSpan, rowSpan: cell.rowSpan})));
        });
        return spans;
    }, [hasHeaders, headersWithSelect]);

    const numDataColumns = hasHeaders ? headers[0].length - 3 : 0;
    const leafLabels = React.useMemo(() => (hasHeaders ? getLeafLabels(headers) : []), [hasHeaders, headers]);
    const labelToIndex = React.useMemo(() => {
        const m = new Map();
        leafLabels.forEach((lbl, i) => m.set(norm(lbl), i));
        return m;
    }, [leafLabels]);

    // For sticky header: calculate top offset for each header row
    const [topOffsets, setTopOffsets] = React.useState([]);
    React.useLayoutEffect(() => {
        if (theadRef.current) {
            const rows = Array.from(theadRef.current.children);
            const offsets = [];
            let currentOffset = 0;
            for (const row of rows) {
                offsets.push(currentOffset);
                currentOffset += row.offsetHeight;
            }
            // Only update state if the offsets have actually changed to prevent an infinite loop
            if (JSON.stringify(offsets) !== JSON.stringify(topOffsets)) {
                setTopOffsets(offsets);
            }
        }
    }, [headerSpans, topOffsets]); // Re-run when headers change

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            <PaginationBar
                page={page}
                hasNext={hasNext}
                hasPrev={hasPrev}
                onNext={onNext}
                onPrev={onPrev}
                loading={loading}
                total={total}
                pageSize={pageSize}
                onPageSizeChange={onPageSizeChange}
                rightExtra={rightExtra}
                leftExtra={leftExtra}
            />

            <div
                style={{
                    border: "1px solid #ccc",
                    borderRadius: "0 0 12px 12px", // Adjust for sticky header UI
                    overflowX: "auto",
                }}
            >
                <table style={{
                    borderCollapse: "separate", borderSpacing: 0,
                    width: "max-content",
                    minWidth: "100%",
                }}>
                    {/* Define column widths */}
                    <colgroup>
                        {/* checkbox column */}
                        <col style={{width: "36px", minWidth: "36px", maxWidth: "40px"}}/>
                        {/* the rest with minimum widths to prevent cells from being too narrow/tall */}
                        {Array.from({length: (headersWithSelect?.[0]?.length ?? 1) - 1}).map((_, i) => (
                            <col key={i} style={{minWidth: i < 3 ? "120px" : "150px"}}/>
                        ))}
                    </colgroup>

                    {/* Header rendering */}
                    <thead ref={theadRef}>
                    {hasHeaders && headerSpans.map((row, rowIndex) => {
                        // Track the actual column index accounting for colSpans AND rowSpans
                        // We need to check which columns are occupied by cells from previous rows
                        let actualColumnIndex = 0;
                        const occupiedColumns = new Set();

                        // Check all previous rows to see which columns have rowSpans extending into this row
                        for (let prevRow = 0; prevRow < rowIndex; prevRow++) {
                            let colIdx = 0;
                            headerSpans[prevRow].forEach(prevCell => {
                                const cellRowSpan = prevCell.rowSpan || 1;
                                const cellColSpan = prevCell.colSpan || 1;

                                // If this cell's rowSpan extends into the current row
                                if (prevRow + cellRowSpan > rowIndex) {
                                    // Mark all columns it occupies
                                    for (let c = colIdx; c < colIdx + cellColSpan; c++) {
                                        occupiedColumns.add(c);
                                    }
                                }
                                colIdx += cellColSpan;
                            });
                        }

                        return (
                            <tr key={`header-row-${rowIndex}`}>
                                {row.map((cell, renderedCellIndex) => {
                                    // Skip occupied columns to find the actual column index
                                    while (occupiedColumns.has(actualColumnIndex)) {
                                        actualColumnIndex++;
                                    }

                                    // Save the current column index before incrementing
                                    const currentColumnIndex = actualColumnIndex;

                                    // Increment by colSpan for next cell
                                    actualColumnIndex += (cell.colSpan || 1);

                                    // Skip the checkbox and first 3 fixed columns (they shouldn't be editable)
                                    const isFixedColumn = currentColumnIndex <= 3;
                                    // Allow editing for all non-fixed columns (including empty cells)
                                    const isEditable = onHeaderEdit && !isFixedColumn;

                                    return (
                                        <th
                                            key={`header-cell-${rowIndex}-${renderedCellIndex}`}
                                            colSpan={cell.colSpan}
                                            rowSpan={cell.rowSpan}
                                            style={{
                                                padding: isEditable ? "4px" : "8px",
                                                textAlign: "center",
                                                background: theme.palette.mode === 'dark' ? '#2c2c2c' : '#f0f0f0',
                                                color: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#1a1a1a',
                                                border: "1px solid #ccc",
                                                // Make all header cells sticky
                                                position: 'sticky',
                                                top: topOffsets[rowIndex] ?? 0,
                                                left: renderedCellIndex === 0 ? 0 : undefined,
                                                // Checkbox column gets highest z-index (sticky top+left), then headers (sticky top), then normal cells
                                                zIndex: renderedCellIndex === 0 ? 103 : 102,
                                            }}
                                        >
                                            {isEditable ? (
                                                <EditableHeader
                                                    value={cell.value}
                                                    isEditing={editingHeader?.row === rowIndex && editingHeader?.col === currentColumnIndex}
                                                    onStartEdit={() => setEditingHeader({row: rowIndex, col: currentColumnIndex})}
                                                    onSave={(newValue) => {
                                                        onHeaderEdit(rowIndex, currentColumnIndex, newValue, cell.colSpan || 1);
                                                        setEditingHeader(null);
                                                    }}
                                                    onCancel={() => setEditingHeader(null)}
                                                />
                                            ) : (
                                                cell.value
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}
                    </thead>
                    <tbody>
                    {results.map((r, rowIdx) => {
                        // --- derive values with key_value_group support ---
                        const annots = r?.data?.annotations || [];
                        const rowValues = new Array(numDataColumns).fill("");
                        const metas = new Array(numDataColumns).fill(null); // {page,bbox} per data col

                        // Fast lookup: column label (normalized) -> colIndex
                        const labelIndex = new Map();
                        leafLabels.forEach((lbl, i) => labelIndex.set(norm(lbl), i));

                        const place = (label, text, page, bbox) => {
                            if (!label) return false;
                            const idx = labelIndex.get(norm(label));
                            if (idx == null) return false;
                            rowValues[idx] = text ?? "";
                            if (Array.isArray(bbox) && bbox.length === 4) metas[idx] = {page: page ?? 1, bbox};
                            return true;
                        };
                        // Fill by matching labels; fall back to first empty slot if unknown
                        const shove = (text, page, bbox) => {
                            const i = rowValues.findIndex(v => v === "");
                            if (i !== -1) {
                                rowValues[i] = text ?? "";
                                if (Array.isArray(bbox) && bbox.length === 4) metas[i] = {page: page ?? 1, bbox};
                            }
                        };
                        annots.forEach(a => {
                            if (a.type === "key_value_group" && Array.isArray(a.items)) {
                                // Expand group items as virtual key_values that align to leaf column labels
                                const page = a.page ?? a.source?.page ?? 1;
                                for (const item of a.items) {
                                    const label = (item?.field?.text)
                                        ?? (typeof item?.field === 'string' ? item.field : '');
                                    const text = item?.value?.text ?? "";
                                    const bbox = item?.value?.bbox || null;
                                    if (!place(label, text, page, bbox)) shove(text, page, bbox);
                                }
                            } else if (a.type === "key_value") {
                                const page = (a.value?.page ?? a.field?.page ?? a.page ?? 1);
                                const label = a.field_text || a.field?.text || a.field?.label || "";
                                const text = a.value?.text || "";
                                const bbox = a.value?.bbox || a.field?.bbox || null;
                                if (!place(label, text, page, bbox)) shove(text, page, bbox);
                            } else if (a.type === "entity" || a.type === "summary_row") {
                                const page = a.page ?? 1;
                                const label = a.entity_type || a.label || "";
                                const text = a.text || "";
                                const bbox = a.bbox || null;
                                if (!place(label, text, page, bbox)) shove(text, page, bbox);
                            } else if (a.type === "table_row") {
                                (a.columns ?? []).forEach(col => {
                                    const page = (col?.page ?? a.page ?? 1);
                                    const label = col?.header || col?.label || ""; // if present
                                    const text = col?.text || "";
                                    const bbox = col?.bbox || null;
                                    if (!place(label, text, page, bbox)) shove(text, page, bbox);
                                });
                            }
                        });


                        const execId = r?.data?.execution_id;
                        const isActive = selectedExecutionId === execId;
                        const isBulkSelected = selectedIds?.has?.(execId);
                        const left = alpha(theme.palette.primary.light, 0.15);
                        const right = alpha(theme.palette.primary.main, 0.08);
                        const activeBg = `linear-gradient(90deg, ${left}, ${right})`;
                        const selectedTdStyle = isActive ? {
                            backgroundImage: activeBg,
                            backgroundSize: "100% 100%",
                            backgroundRepeat: "no-repeat",
                            backgroundClip: "padding-box",
                            backgroundOrigin: "padding-box",
                        } : (isBulkSelected ? {
                            backgroundColor: alpha(theme.palette.primary.main, 0.06)
                        } : {});

                        return (
                            <tr key={execId || r.fileName}>
                                {/* Selection checkbox */}
                                <td
                                    style={{
                                        padding: 0,
                                        border: "1px solid #ccc",
                                        textAlign: 'center',
                                        width: "36px",
                                        minWidth: "36px",
                                        maxWidth: "40px",
                                        // sticky select column
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 101,
                                        background: theme.palette.background.paper,
                                        ...selectedTdStyle,
                                    }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={!!(selectedIds?.has?.(execId))}
                                        onChange={(e) => onToggleRow?.(execId, e.target.checked)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </td>
                                {/* Extracted */}
                                <td style={{
                                    padding: "6px",
                                    border: "1px solid #ccc",
                                    whiteSpace: 'nowrap', ...selectedTdStyle
                                }}>
                                    {fmt(r.created_at)}
                                </td>
                                {/* Updated */}
                                <td style={{
                                    padding: "6px",
                                    border: "1px solid #ccc",
                                    whiteSpace: 'nowrap', ...selectedTdStyle
                                }}>
                                    {fmt(r.updated_at)}
                                </td>
                                {/* Filename */}
                                <td style={{padding: "6px", border: "1px solid #ccc", ...selectedTdStyle}}>
                                    <Button variant="text" size="small"
                                            onClick={() => onFilenameClick?.(execId, r?.data?.annotations, r.fileName)}>
                                        {r.fileName}
                                    </Button>
                                </td>

                                {rowValues.map((val, i) => {
                                    const executionId = execId;
                                    const baseMeta = metas[i];
                                    const override = overrides?.[executionId]?.[i] || null;
                                    const meta = override || baseMeta; // prefer override
                                    const rowIsLoading = loading === true;
                                    const isEditing =
                                        editingCell &&
                                        (editingCell.executionId
                                            ? editingCell.executionId === executionId
                                            : editingCell.fileName === r.fileName) &&
                                        editingCell.index === i;

                                    const isExtracting =
                                        extractingCell &&
                                        extractingCell.executionId === executionId &&
                                        extractingCell.colIndex === i;

                                    // Check if this cell is hovered from the PDF
                                    const cellKey = meta?.page && meta?.bbox ? `${meta.page}_entity_${meta.bbox.join('_')}` : null;
                                    const isCellHovered = cellKey && hoveredKeyExternal === cellKey;

                                    return (
                                        <td
                                            key={`cell-${rowIdx}-${i}`}
                                            style={{
                                                padding: isEditing ? "4px" : "6px",
                                                border: "1px solid #ccc",
                                                position: "relative",
                                                cursor: rowIsLoading ? "default" : "pointer",
                                                backgroundColor: isCellHovered ? 'rgba(59, 188, 167, 0.3)' : undefined,
                                                transition: 'background-color 0.2s ease',
                                                minWidth: isEditing ? "200px" : undefined,
                                                whiteSpace: 'normal',
                                                wordBreak: "break-word",
                                                maxWidth: "350px",
                                                ...selectedTdStyle,
                                            }}
                                            onClick={() => {
                                                if (rowIsLoading || isEditing) return;
                                                // delay jiggle so dbl-click can cancel it
                                                window.clearTimeout(clickTimerRef.current);
                                                clickTimerRef.current = window.setTimeout(() => {
                                                    const m = meta;
                                                    if (m && onCellClick) {
                                                        onCellClick({
                                                            executionId,
                                                            page: m.page,
                                                            bbox: m.bbox,
                                                            fileObj: {name: r.fileName},
                                                        });
                                                    }
                                                }, 250);
                                            }}
                                            onMouseEnter={() => {
                                                if (rowIsLoading || isEditing) return;
                                                const m = meta;
                                                if (m && m.page && m.bbox && onCellHover) {
                                                    onCellHover({
                                                        executionId,
                                                        page: m.page,
                                                        bbox: m.bbox,
                                                        fileObj: {name: r.fileName},
                                                    });
                                                }
                                            }}
                                            onMouseLeave={() => {
                                                if (onCellHover) {
                                                    onCellHover(null);
                                                }
                                            }}
                                            onDoubleClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                // cancel pending single-click jiggle
                                                window.clearTimeout(clickTimerRef.current);
                                                if (rowIsLoading || isEditing) return;
                                                const shown =
                                                    (editedValues[executionId ?? r.fileName]?.[i] ?? val) ?? "";
                                                onCellDoubleClick?.({
                                                    executionId,
                                                    fileName: r.fileName,
                                                    index: i,
                                                    value: shown,
                                                });
                                            }}
                                        >
                                            {/* Redraw pill (hidden until hover; fully opaque when visible) */}
                                            {!rowIsLoading && !isEditing && (
                                                <>
                                                    <Tooltip title={t('historyTable.redraw')} arrow>
                                                        <div
                                                            className="cell-redraw"
                                                            role="button"
                                                            tabIndex={0}
                                                            aria-label={t('historyTable.redraw')}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const m = meta || null;
                                                                onCellRedraw?.({
                                                                    executionId,
                                                                    page: m?.page,
                                                                    bbox: m?.bbox,
                                                                    fileObj: {name: r.fileName},
                                                                    colIndex: i,
                                                                });
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter" || e.key === " ") {
                                                                    e.preventDefault();
                                                                    const m = meta || null;
                                                                    onCellRedraw?.({
                                                                        executionId,
                                                                        page: m?.page,
                                                                        bbox: m?.bbox,
                                                                        fileObj: {name: r.fileName},
                                                                        colIndex: i,
                                                                    });
                                                                }
                                                            }}
                                                            style={{
                                                                position: "absolute",
                                                                top: 6,
                                                                right: 6,
                                                                fontSize: 11,
                                                                padding: "4px 8px",
                                                                borderRadius: 12,
                                                                background: (theme?.palette?.mode === 'dark') ? '#1f2937' : '#ffffff',
                                                                color: (theme?.palette?.mode === 'dark') ? '#e5e7eb' : '#111111',
                                                                border: (theme?.palette?.mode === 'dark')
                                                                    ? "1px solid rgba(255,255,255,0.22)"
                                                                    : "1px solid rgba(0,0,0,0.22)",
                                                                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                                                                fontWeight: 600,
                                                                zIndex: 20,
                                                                transition: "opacity 120ms ease-in-out",
                                                                opacity: 0,
                                                                pointerEvents: "none",
                                                                cursor: "pointer",
                                                                lineHeight: 1
                                                            }}
                                                        >
                                                            ↺
                                                        </div>
                                                    </Tooltip>
                                                    <style>{`
      td:hover .cell-redraw,
      td:focus-within .cell-redraw {
        opacity: 1 !important;
        pointer-events: auto !important;
      }
      @media (hover: none) {
        .cell-redraw {
          opacity: 1 !important;
          pointer-events: auto !important;
        }
      }
    `}</style>
                                                </>
                                            )}

                                            <EditableCell
                                                value={
                                                    isEditing
                                                        ? editValue
                                                        : (editedValues[executionId ?? r.fileName]?.[i] ?? val)
                                                }
                                                isEditing={isEditing}
                                                onEditStart={() => {
                                                    const shown =
                                                        (editedValues[executionId ?? r.fileName]?.[i] ?? val) ?? "";
                                                    setEditingCell({executionId, fileName: r.fileName, index: i});
                                                    setEditValue(shown);
                                                }}
                                                onEditChange={(e) => setEditValue(e.target.value)}
                                                onConfirm={handleConfirm}
                                                onCancel={handleCancel}
                                                isLoading={isExtracting || (processingCell?.executionId === executionId && processingCell?.colIndex === i)}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                    </tbody>

                </table>
            </div>


            {/*<PaginationBar*/}
            {/*    page={page}*/}
            {/*    hasNext={hasNext}*/}
            {/*    hasPrev={hasPrev}*/}
            {/*    onNext={onNext}*/}
            {/*    onPrev={onPrev}*/}
            {/*    loading={loading}*/}
            {/*    total={total}*/}
            {/*    pageSize={pageSize}*/}
            {/*    onPageSizeChange={onPageSizeChange}*/}
            {/*    rightExtra={rightExtra}*/}
            {/*    leftExtra={leftExtra}*/}
            {/*/>*/}

        </Box>
    );
}
